import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { UserRefSchema } from './user.js';

/**
 * Wiki проекта (docs/14).
 *
 * Wiki независима от git: страницы живут в БД, а обмен с внешним миром идёт
 * импортом и экспортом markdown-архивов администратором проекта.
 */

export const WikiStatusSchema = z.enum(['draft', 'published']);
export type WikiStatus = z.infer<typeof WikiStatusSchema>;

/** Метка страницы: короткая свободная пометка, как у задач, но своя. */
export const WikiLabelSchema = z
  .string()
  .trim()
  .min(1, 'Метка не может быть пустой')
  .max(40, 'Не больше 40 символов');

/** Узел дерева: без тела, чтобы список был дешёвым. */
export const WikiPageSummarySchema = z.object({
  id: UuidSchema,
  projectId: UuidSchema,
  parentId: UuidSchema.nullable(),
  title: z.string(),
  slug: z.string(),
  status: WikiStatusSchema,
  /** Порядок среди соседей (LexoRank, как у задач). */
  rank: z.string(),
  labels: z.array(z.string()),
  /** Путь в архиве обмена; null — страница ещё не выгружалась. */
  sourcePath: z.string().nullable(),
  updatedAt: IsoDateTimeSchema,
  createdAt: IsoDateTimeSchema,
});
export type WikiPageSummary = z.infer<typeof WikiPageSummarySchema>;

export const WikiPageSchema = WikiPageSummarySchema.extend({
  body: z.string(),
  /** Текущий номер версии: по нему клиент ловит одновременную правку. */
  version: z.number().int().positive(),
  updatedBy: UserRefSchema.nullable(),
  createdBy: UserRefSchema.nullable(),
});
export type WikiPage = z.infer<typeof WikiPageSchema>;

export const WikiPageListSchema = z.object({
  items: z.array(WikiPageSummarySchema),
});
export type WikiPageList = z.infer<typeof WikiPageListSchema>;

/** Заголовок обязателен; тело может быть пустым у черновика (docs/14 §4.5). */
const TitleSchema = z
  .string()
  .trim()
  .min(1, 'Введите заголовок')
  .max(200, 'Не больше 200 символов');

const BodySchema = z.string().max(262_144, 'Не больше 256 КБ');

const LabelsSchema = z.array(WikiLabelSchema).max(20, 'Не больше 20 меток');

export const CreateWikiPageBodySchema = z
  .object({
    title: TitleSchema,
    body: BodySchema.default(''),
    parentId: UuidSchema.nullable().default(null),
    status: WikiStatusSchema.default('draft'),
    labels: LabelsSchema.default([]),
  })
  .strict();
export type CreateWikiPageBody = z.infer<typeof CreateWikiPageBodySchema>;

export const UpdateWikiPageBodySchema = z
  .object({
    title: TitleSchema.optional(),
    body: BodySchema.optional(),
    status: WikiStatusSchema.optional(),
    labels: LabelsSchema.optional(),
    /** Комментарий к правке — «зачем», как в Confluence. */
    message: z.string().max(500).optional(),
    /**
     * Версия, которую правил клиент. Если она устарела, сервер отвечает
     * конфликтом: чужая правка не затирается молча (docs/14 §4.6).
     */
    version: z.number().int().positive().optional(),
  })
  .strict();
export type UpdateWikiPageBody = z.infer<typeof UpdateWikiPageBodySchema>;

/** Перемещение: смена родителя и/или позиции среди соседей. */
export const MoveWikiPageBodySchema = z
  .object({
    parentId: UuidSchema.nullable().optional(),
    /** Позиция среди детей нового родителя; без неё — в конец. */
    position: z.number().int().min(0).optional(),
  })
  .strict();
export type MoveWikiPageBody = z.infer<typeof MoveWikiPageBodySchema>;

export const WikiPageVersionSchema = z.object({
  id: UuidSchema,
  pageId: UuidSchema,
  version: z.number().int().positive(),
  title: z.string(),
  body: z.string(),
  /** Откуда пришла версия: правка в UI, API, импорт или откат. */
  source: z.enum(['ui', 'api', 'import', 'revert']),
  message: z.string(),
  author: UserRefSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type WikiPageVersion = z.infer<typeof WikiPageVersionSchema>;

export const WikiVersionListSchema = z.object({
  items: z.array(WikiPageVersionSchema),
});
export type WikiVersionList = z.infer<typeof WikiVersionListSchema>;

/** Строка diff: одинаковый контекст или добавление/удаление. */
export const WikiDiffLineSchema = z.object({
  kind: z.enum(['same', 'added', 'removed']),
  text: z.string(),
  /** Номер строки в левой (старой) версии; null для добавленных. */
  left: z.number().int().positive().nullable(),
  /** Номер строки в правой (новой) версии; null для удалённых. */
  right: z.number().int().positive().nullable(),
});
export type WikiDiffLine = z.infer<typeof WikiDiffLineSchema>;

export const WikiDiffSchema = z.object({
  from: z.number().int().positive(),
  to: z.number().int().positive(),
  lines: z.array(WikiDiffLineSchema),
  /** Сколько строк добавлено и удалено — для заголовка экрана. */
  added: z.number().int().nonnegative(),
  removed: z.number().int().nonnegative(),
});
export type WikiDiff = z.infer<typeof WikiDiffSchema>;

export const WikiRevertBodySchema = z
  .object({
    version: z.number().int().positive(),
  })
  .strict();
export type WikiRevertBody = z.infer<typeof WikiRevertBodySchema>;

export const WikiSearchQuerySchema = z.object({
  q: z.string().trim().min(2, 'Введите хотя бы два символа').max(200),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type WikiSearchQuery = z.infer<typeof WikiSearchQuerySchema>;

export const WikiSearchResultSchema = z.object({
  id: UuidSchema,
  title: z.string(),
  slug: z.string(),
  /** Фрагмент тела вокруг совпадения — для подсветки в списке. */
  excerpt: z.string(),
  status: WikiStatusSchema,
  updatedAt: IsoDateTimeSchema,
});
export type WikiSearchResult = z.infer<typeof WikiSearchResultSchema>;

export const WikiSearchResponseSchema = z.object({
  items: z.array(WikiSearchResultSchema),
});
export type WikiSearchResponse = z.infer<typeof WikiSearchResponseSchema>;

/** Журнал обмена: импорт или экспорт (docs/14 §5). */
export const WikiTransferSchema = z.object({
  id: UuidSchema,
  direction: z.enum(['import', 'export']),
  actor: UserRefSchema.nullable(),
  fileName: z.string(),
  fileSize: z.number().int().nonnegative(),
  pagesTotal: z.number().int().nonnegative(),
  pagesCreated: z.number().int().nonnegative(),
  pagesUpdated: z.number().int().nonnegative(),
  pagesSkipped: z.number().int().nonnegative(),
  pagesConflict: z.number().int().nonnegative(),
  status: z.enum(['ok', 'error']),
  error: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  finishedAt: IsoDateTimeSchema.nullable(),
});
export type WikiTransfer = z.infer<typeof WikiTransferSchema>;

export const WikiTransferListSchema = z.object({
  items: z.array(WikiTransferSchema),
});
export type WikiTransferList = z.infer<typeof WikiTransferListSchema>;

/** Действие плана импорта (docs/14 §5.4). */
export const WikiImportActionSchema = z.enum([
  'create',
  'update',
  'skip',
  'conflict',
  'invalid',
]);
export type WikiImportAction = z.infer<typeof WikiImportActionSchema>;

export const WikiImportPlanItemSchema = z.object({
  action: WikiImportActionSchema,
  /** Путь внутри архива. */
  path: z.string(),
  title: z.string(),
  /** Существующая страница, которую обновляем; null — создаём новую. */
  pageId: UuidSchema.nullable(),
  slug: z.string(),
  reason: z.string().nullable(),
});
export type WikiImportPlanItem = z.infer<typeof WikiImportPlanItemSchema>;

export const WikiImportPlanSchema = z.object({
  total: z.number().int().nonnegative(),
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  conflict: z.number().int().nonnegative(),
  invalid: z.number().int().nonnegative(),
  /** Постраничный план: показывается в предпросмотре. */
  pages: z.array(WikiImportPlanItemSchema),
  /** Ошибки разбора файлов: не мешают применить остальные. */
  errors: z.array(z.object({ path: z.string(), reason: z.string() })),
});
export type WikiImportPlan = z.infer<typeof WikiImportPlanSchema>;

export const WikiImportResponseSchema = z.object({
  /** true — предпросмотр, данные не менялись. */
  dryRun: z.boolean(),
  plan: WikiImportPlanSchema,
  /** Заполняется только при применении. */
  transfer: WikiTransferSchema.nullable(),
});
export type WikiImportResponse = z.infer<typeof WikiImportResponseSchema>;

/** Тело запроса импорта: файл приходит multipart-ом, флаги — полями формы. */
export const WikiImportFieldsSchema = z.object({
  dryRun: z
    .enum(['true', 'false', '1', '0'])
    .default('true')
    .transform((value) => value === 'true' || value === '1'),
  force: z
    .enum(['true', 'false', '1', '0'])
    .default('false')
    .transform((value) => value === 'true' || value === '1'),
  parentId: UuidSchema.optional(),
});
export type WikiImportFields = z.infer<typeof WikiImportFieldsSchema>;

export const WikiExportQuerySchema = z.object({
  /** Поддерево: выгрузить только эту страницу и её потомков. */
  pageId: UuidSchema.optional(),
});
export type WikiExportQuery = z.infer<typeof WikiExportQuerySchema>;

export const WikiTransferListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type WikiTransferListQuery = z.infer<typeof WikiTransferListQuerySchema>;

/** Вложение страницы вики. */
export const WikiAttachmentSchema = z.object({
  id: UuidSchema,
  pageId: UuidSchema,
  filename: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().positive(),
  uploader: UserRefSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type WikiAttachment = z.infer<typeof WikiAttachmentSchema>;

export const WikiAttachmentListSchema = z.object({
  items: z.array(WikiAttachmentSchema),
});
export type WikiAttachmentList = z.infer<typeof WikiAttachmentListSchema>;

/** Комментарий страницы: та же форма, что у задачи, но свой ресурс. */
export const WikiCommentSchema = z.object({
  id: UuidSchema,
  pageId: UuidSchema,
  parentId: UuidSchema.nullable(),
  body: z.string(),
  author: UserRefSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type WikiComment = z.infer<typeof WikiCommentSchema>;

export const WikiCommentListSchema = z.object({
  items: z.array(WikiCommentSchema),
});
export type WikiCommentList = z.infer<typeof WikiCommentListSchema>;

export const CreateWikiCommentBodySchema = z
  .object({
    body: z.string().trim().min(1, 'Введите комментарий').max(10_000, 'Не больше 10000 символов'),
    parentId: UuidSchema.nullable().default(null),
  })
  .strict();
export type CreateWikiCommentBody = z.infer<typeof CreateWikiCommentBodySchema>;


// --- Шаблоны страниц (docs/14 §9a) ---

export const WIKI_TEMPLATE_MAX_BYTES = 262_144;

export const WikiTemplateSchema = z.object({
  /** UUID проектного шаблона или `system:<key>` у системного. */
  id: z.string().min(1),
  key: z.string().min(1),
  name: z.string(),
  body: z.string(),
  isSystem: z.boolean(),
  updatedAt: IsoDateTimeSchema.nullable(),
});
export type WikiTemplate = z.infer<typeof WikiTemplateSchema>;

export const WikiTemplateListSchema = z.object({ items: z.array(WikiTemplateSchema) });
export type WikiTemplateList = z.infer<typeof WikiTemplateListSchema>;

const TemplateNameSchema = z.string().trim().min(1, 'Введите название').max(200);
const TemplateBodySchema = z.string().max(WIKI_TEMPLATE_MAX_BYTES, 'Не больше 256 КБ');

export const CreateWikiTemplateBodySchema = z
  .object({ name: TemplateNameSchema, body: TemplateBodySchema.default('') })
  .strict();
export type CreateWikiTemplateBody = z.infer<typeof CreateWikiTemplateBodySchema>;

export const UpdateWikiTemplateBodySchema = z
  .object({
    name: TemplateNameSchema.optional(),
    body: TemplateBodySchema.optional(),
  })
  .strict();
export type UpdateWikiTemplateBody = z.infer<typeof UpdateWikiTemplateBodySchema>;

/** Публичный список страниц: пагинация и фильтры (docs/14 §9). */
export const PublicWikiListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  parentId: UuidSchema.optional(),
  status: WikiStatusSchema.optional(),
});
export type PublicWikiListQuery = z.infer<typeof PublicWikiListQuerySchema>;
